import Accordion from "../UI/Accordion";
import VerticalGanttChartTransportationIcon from "../ganttChart/verticalGanttChart/VerticalGanttChartTransportationIcon";
import TimePicker from "../UI/input/TimePicker";
import { ReactComponent as CloseIcon } from "../../assets/icons/x_black.svg";
import { EmployeeTransport, TransportationType } from "../../types/ganttChart";
import labels from "../../utils/labels";
import { useAppSelector } from "../../store/hooks";
import EmployeeSettingsStyles from "../../styles/employeeSettings.module.css";
import inputStyles from "../../styles/UI/inputFields.module.css";
import useDataSelector from "../../hooks/useDataSelector";

type TransportOption = {
  id: TransportationType;
  title: string;
};

const EmployeeTransportListItem = ({
  transport,
  options,
  onTypeChange,
  onStartChange,
  onEndChange,
  canDelete,
  onDelete,
  disableAll,
  disableTimeWindow,
  minTime,
  maxTime,
  excludedStartTimes,
  excludedEndTimes,
  typeError,
  startError,
  endError,
}: {
  transport: EmployeeTransport;
  options: TransportOption[];
  onTypeChange: (newType: TransportationType) => void;
  onStartChange: (newStart: string) => void;
  onEndChange: (newEnd: string) => void;
  canDelete: boolean;
  onDelete: () => void;
  disableAll?: boolean;
  disableTimeWindow?: boolean;
  minTime?: string;
  maxTime?: string;
  excludedStartTimes?: string[];
  excludedEndTimes?: string[];
  typeError?: boolean;
  startError?: boolean;
  endError?: boolean;
}) => {
  const { manualEdits } = useAppSelector(
    (state) => state.employeeSettingsSlice,
  );
  const { employeeShiftId } = useAppSelector(
    (state) => state.drawerSlice.selectedEmployee!,
  );
  const { dataToUse } = useDataSelector();
  const manualShiftChanges = dataToUse?.employees.find(
    (e) => e.shiftSchedule.idShift === employeeShiftId,
  )?.manualShiftChanges;

  const isTimeWindowDisabled = disableAll || disableTimeWindow;

  const manualTransportTypeEdits =
    manualEdits[employeeShiftId]?.transportationType ??
    manualShiftChanges?.transportationType;

  const displayOptionIndex = options.findIndex(
    (option) => option.id === transport.transportationType,
  );

  const transportIcon = (option: TransportOption, isSelected: boolean) => {
    const iconColor = disableAll
      ? "var(--col-text-secondary)"
      : isSelected
        ? "var(--col-text-invert)"
        : "var(--col-text-secondary)";

    return (
      <VerticalGanttChartTransportationIcon
        type={option.id}
        color={iconColor}
        size={16}
      />
    );
  };

  return (
    <div
      className={`flex gap-[var(--spacing-md)] items-start
        ${manualTransportTypeEdits && EmployeeSettingsStyles.editedSetting}`}
    >
      <Accordion
        onChange={(newOption: TransportOption) => onTypeChange(newOption.id)}
        options={options}
        variant="PRIMARY"
        defaultText={labels.chooseTransport}
        label={labels.transportType}
        classes="w-full"
        displayOption={displayOptionIndex}
        leadingElement={transportIcon}
        labelKey="title"
        disabled={disableAll}
        helperText={manualTransportTypeEdits && labels.manuallyEditedTransport}
        error={typeError}
      />
      <div className="flex gap-[var(--spacing-sm)] w-full">
        <TimePicker
          id="transport-start-time"
          value={transport.start}
          onTimeChange={(newStart) => onStartChange(newStart)}
          label={labels.startTimeTransport}
          classes="w-full"
          disabled={isTimeWindowDisabled}
          min={minTime}
          max={maxTime}
          excludedOptions={excludedStartTimes}
          error={startError}
        />
        <TimePicker
          id="transport-end-time"
          value={transport.end}
          onTimeChange={(newEnd) => onEndChange(newEnd)}
          label={labels.endTimeTransport}
          classes="w-full"
          disabled={isTimeWindowDisabled}
          min={minTime}
          max={maxTime}
          excludedOptions={excludedEndTimes}
          error={endError}
        />
      </div>
      {canDelete && (
        <div className="flex flex-col gap-[var(--spacing-xs)]">
          <span className={`pl-2 invisible ${inputStyles.label}`}>&nbsp;</span>
          <div className="h-[40px] flex items-center">
            <CloseIcon
              width={20}
              height={20}
              onClick={onDelete}
              className="cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTransportListItem;
